import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/errors';
import { useTenantLocation } from '../context/LocationContext';
import { api } from '../lib/api';
import { LocationHealthStrip } from '../components/dashboard/LocationHealthStrip';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Package, 
  Users, 
  ShoppingCart,
  AlertTriangle,
  ArrowRight,
  Loader2,
  Database,
  TrendingUp
} from 'lucide-react';
import { cn } from '../lib/utils';

export default function Dashboard() {
  const { token, user } = useAuth();
  const { currentLocationId, currentLocation } = useTenantLocation();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchData = async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        api.getDashboardStats(token),
        api.getRecentActivity(token, 8)
      ]);
      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // Re-fetch whenever the merchant switches locations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentLocationId]);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await api.triggerSync(token, 'all');
      toast.success('Full sync completed successfully!');
      fetchData();
    } catch (error) {
      toast.error('Sync failed. Check logs for details.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await api.seedData(token);
      toast.success('Demo data seeded successfully!');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load demo data'));
    } finally {
      setSeeding(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-rose-500" />;
      case 'partial': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      default: return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: 'status-success',
      failed: 'status-error',
      partial: 'status-pending',
      pending: 'status-synced'
    };
    return styles[status] || styles.pending;
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Sync Dashboard
          </h1>
          <p className="text-slate-400 mt-1">
            Monitor and manage your UnTill to Odoo synchronization
            {currentLocation ? (
              <> · <span className="text-blue-300">{currentLocation.location_name}</span></>
            ) : (
              <> · <span className="text-blue-300">All locations</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {user?.account_mode !== 'live' && <Button 
            variant="outline" 
            onClick={handleSeedData}
            disabled={seeding}
            className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
            data-testid="seed-data-btn"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
            Load Demo Data
          </Button>}
          <Button 
            onClick={handleSyncAll}
            disabled={syncing}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            data-testid="sync-all-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync All
          </Button>
        </div>
      </div>

      {/* Location Health Strip (hidden for single-venue tenants) */}
      <LocationHealthStrip />

      {/* Sync Health Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sync Pipeline Visual */}
        <Card className="lg:col-span-2 bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              Sync Pipeline Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4 p-6 rounded-lg bg-slate-800/30">
              {/* UnTill Source */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-blue-400">U</span>
                </div>
                <p className="text-white font-medium">UnTill POS</p>
                <p className="text-xs text-slate-500">Source</p>
              </div>
              
              {/* Connection Arrow */}
              <div className="flex-1 flex items-center">
                <div className="flex-1 h-px bg-gradient-to-r from-blue-500 to-emerald-500" />
                {(() => {
                  const st = stats?.sync_health_status || 'no_recent_activity';
                  const cls =
                    st === 'healthy'
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                      : st === 'degraded'
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                      : st === 'failing'
                      ? 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                      : 'bg-slate-500/10 border-slate-500/30 text-slate-300';
                  const label =
                    st === 'no_recent_activity'
                      ? 'No syncs today'
                      : `${stats?.sync_health_percentage ?? 0}% Health`;
                  return (
                    <div className={`px-4 py-2 rounded-full border ${cls}`} data-testid="pipeline-health-chip">
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                  );
                })()}
                <div className="flex-1 h-px bg-gradient-to-r from-emerald-500 to-blue-500" />
              </div>

              {/* Odoo Destination */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-emerald-400">O</span>
                </div>
                <p className="text-white font-medium">Odoo ERP</p>
                <p className="text-xs text-slate-500">Destination</p>
              </div>
            </div>

            {/* Today's Sync Progress */}
            <div className="mt-6 grid grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg bg-slate-800/30">
                <p className="text-2xl font-bold text-white">{stats?.total_syncs_today || 0}</p>
                <p className="text-xs text-slate-500">Total Today</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-2xl font-bold text-emerald-400">{stats?.successful_syncs || 0}</p>
                <p className="text-xs text-slate-500">Successful</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-rose-500/10 border border-rose-500/20">
                <p className="text-2xl font-bold text-rose-400">{stats?.failed_syncs || 0}</p>
                <p className="text-xs text-slate-500">Failed</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-2xl font-bold text-amber-400">{stats?.pending_syncs || 0}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sync Health Score */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-heading text-white">Sync Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="relative pt-4">
              {(() => {
                const st = stats?.sync_health_status || 'no_recent_activity';
                const showBareNumber = st !== 'no_recent_activity';
                const numColor =
                  st === 'healthy'
                    ? 'text-white'
                    : st === 'degraded'
                    ? 'text-amber-300'
                    : st === 'failing'
                    ? 'text-rose-300'
                    : 'text-slate-400';
                const barColor =
                  st === 'healthy'
                    ? 'bg-emerald-500/70'
                    : st === 'degraded'
                    ? 'bg-amber-500/70'
                    : st === 'failing'
                    ? 'bg-rose-500/70'
                    : 'bg-slate-600';
                return (
                  <>
                    <div className="text-center mb-4">
                      {showBareNumber ? (
                        <>
                          <span className={`text-5xl font-bold ${numColor}`} data-testid="sync-health-number">
                            {stats?.sync_health_percentage ?? 0}
                          </span>
                          <span className="text-2xl text-slate-400">%</span>
                        </>
                      ) : (
                        <span
                          className="inline-block px-3 py-1 rounded-md bg-slate-800/60 border border-slate-700 text-slate-300 text-sm font-medium"
                          data-testid="sync-health-idle-badge"
                        >
                          Idle · no syncs today
                        </span>
                      )}
                    </div>
                    <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
                      <div
                        className={`h-full ${barColor} transition-all`}
                        style={{ width: `${showBareNumber ? stats?.sync_health_percentage ?? 0 : 0}%` }}
                      />
                    </div>
                    {stats?.sync_health_caption && (
                      <p
                        className="mt-3 text-xs text-slate-400 leading-relaxed text-center"
                        data-testid="sync-health-caption"
                      >
                        {stats.sync_health_caption}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Last Sync</span>
                <span className="text-white font-mono">{formatTime(stats?.last_sync_time)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Inventory Alerts</span>
                <Badge className={stats?.inventory_alerts > 0 ? 'status-error' : 'status-success'}>
                  {stats?.inventory_alerts || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Products Synced</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.products_synced || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Customers Synced</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.customers_synced || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Sales Synced</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.sales_synced || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Inventory Alerts</p>
                <p className="text-3xl font-bold text-white mt-1">{stats?.inventory_alerts || 0}</p>
              </div>
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center",
                stats?.inventory_alerts > 0 ? "bg-rose-500/10" : "bg-slate-800"
              )}>
                <AlertTriangle className={cn("w-6 h-6", stats?.inventory_alerts > 0 ? "text-rose-400" : "text-slate-500")} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading text-white">Recent Sync Activity</CardTitle>
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="view-all-logs-btn">
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentActivity.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {user?.account_mode === 'live' ? 'No sync activity yet. Sync from unTill or add a location to get started.' : 'No sync activity yet. Click "Load Demo Data" to populate sample data.'}
              </div>
            ) : (
              recentActivity.map((log, index) => (
                <div 
                  key={log.id} 
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors animate-slide-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`activity-item-${index}`}
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(log.status)}
                    <div>
                      <p className="text-white font-medium capitalize">{log.sync_type === 'account_mode' ? 'Environment change' : `${log.sync_type} Sync`}</p>
                      <p className="text-slate-500 text-sm">{log.source} → {log.destination}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-white text-sm">{log.records_processed} records</p>
                      <p className="text-slate-500 text-xs">{log.duration_ms}ms</p>
                    </div>
                    <Badge className={getStatusBadge(log.status)}>{log.status}</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
